import { useClientPortalAPI, useQueryParams } from "@certn/api";
import { Text, Stack, Link } from "@certn/cascadia";
import {
  Container,
  DangerTag,
  DataTable,
  DefaultTag,
  Loader,
  MediaQuery,
  Pagination,
  SuccessTag,
} from "@certn/cascadia-react";
import {
  orderingParamToSortDescriptor,
  sortDescriptorToOrderingParam,
} from "@certn/cascadia-react/components/data-tables/utils";
import { formatDate } from "@certn/utils";
import { css } from "@emotion/css";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { Trans } from "react-i18next";

import { HistoricalInvoicesActionsDropdownMenu } from "./components/HistoricalInvoicesActionsDropdownMenu";
import { HistoricalTableDataCard } from "./components/HistoricalTableDataCard";
import { DateRangeFilter } from "../../legacy-invoices/filters/DateRangeFilter";
import { InvoiceStatusFilter } from "../../legacy-invoices/filters/InvoiceStatusFilter";
import { ProfileNameFilter } from "../../legacy-invoices/filters/ProfileNameFilter";
import { ClearFilters } from "../../../components/filters/ClearFilters";
import { useCanViewClientPortalPricing } from "../../../components/permissions/Permissions";
import { LEGACY_INVOICE_STATUS } from "../../../constants";
import { useClientAccount } from "../../../hooks/useClientAccount";
import { formatNullableCurrency } from "../../../utils/formatNullableCurrency";

interface HistoricalInvoice {
  id: string;
  identifier: string;
  number: string;
  issued_at: string | null;
  status: string;
  total_amount: string | null;
  billing_profile: {
    profile_name: string | null;
  };
}

export function HistoricalInvoicesTab() {
  const { client } = useClientAccount();
  const canViewClientPortalPricing = useCanViewClientPortalPricing();
  const { setParam, flatParams } = useQueryParams();
  const [profileNameOptions, setProfileNameOptions] = useState([
    { id: "", name: "" },
  ]);

  const { data, isLoading } = useClientPortalAPI(
    `/api/internal/client-portal/customer-accounts/${client?.id}/billing-profile-legacy-invoices/`,
    {
      page: flatParams.page,
      page_size: flatParams.page_size,
      ordering: flatParams.ordering,
      billing_profile__id__in: flatParams.profile_ids,
      issued_at__range: flatParams.date_range,
      status: flatParams.status || "OPEN, PAID",
    }
  );

  useEffect(() => {
    if (data?.filters) {
      data.filters.forEach((filter) => {
        if (filter.key === "billing_profile__id__in") {
          setProfileNameOptions(
            filter.options.map((option) => {
              return {
                id: option.key,
                name: option.label,
              };
            })
          );
        }
      });
    }
  }, [data]);

  const formatInvoiceTotal = (totalAmount: string | null) => {
    return formatNullableCurrency(totalAmount, {
      currency: "CAD",
    });
  };

  const invoiceColumns = [
    {
      name: t("Invoice #"),
      key: "number",
      testID: "invoice-number-header",
      isRowHeader: true,
      allowsSorting: true,
      render(item: HistoricalInvoice) {
        return item.number;
      },
    },
    {
      name: t("Billing profile"),
      key: "billing_profile__profile_name",
      isRowHeader: true,
      testID: "billing-profile-header",
      render(item: HistoricalInvoice) {
        return item.billing_profile?.profile_name || "-";
      },
    },
    ...(canViewClientPortalPricing
      ? [
          {
            name: t("Total"),
            key: "total",
            isRowHeader: true,
            testID: "total-header",
            render(item: HistoricalInvoice) {
              return formatInvoiceTotal(item.total_amount);
            },
          },
        ]
      : []),
    {
      name: t("Issued at"),
      key: "issued_at",
      isRowHeader: true,
      allowsSorting: true,
      testID: "issued-at-header",
      render(item: HistoricalInvoice) {
        return formatDate(item.issued_at, "PP");
      },
    },
    {
      name: t("Status"),
      key: "status",
      isRowHeader: true,
      allowsSorting: true,
      testID: "status-header",
      render(item: HistoricalInvoice) {
        return getInvoiceLabel(item.status);
      },
    },
    {
      name: t("Actions"),
      key: "actions",
      isAction: true,
      render(invoice: HistoricalInvoice) {
        return <HistoricalInvoicesActionsDropdownMenu invoiceId={invoice.id} />;
      },
    },
  ];

  const getInvoiceLabel = (status: string) => {
    switch (status) {
      case LEGACY_INVOICE_STATUS.draft:
        return (
          <DefaultTag key={LEGACY_INVOICE_STATUS.draft}>
            {t("Draft")}
          </DefaultTag>
        );
      case LEGACY_INVOICE_STATUS.open:
        return (
          <DefaultTag key={LEGACY_INVOICE_STATUS.open}>{t("Open")}</DefaultTag>
        );
      case LEGACY_INVOICE_STATUS.paid:
        return (
          <SuccessTag key={LEGACY_INVOICE_STATUS.paid}>{t("Paid")}</SuccessTag>
        );
      case LEGACY_INVOICE_STATUS.voided:
        return (
          <DangerTag key={LEGACY_INVOICE_STATUS.voided}>
            {t("Voided")}
          </DangerTag>
        );
      case LEGACY_INVOICE_STATUS.pending:
        return (
          <DangerTag key={LEGACY_INVOICE_STATUS.pending}>
            {t("Pending")}
          </DangerTag>
        );
      default:
        return "-";
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <MediaQuery sm type="hide">
        <Stack orientation="horizontal" gap="space-2">
          <Text color="text-weak">
            <Trans>
              View the status and details of your historical invoices.
            </Trans>
            <br />
            <br />
          </Text>
          <Link
            target="_blank"
            href="https://portal.certn.co/hc/en-us/articles/29007204255507-Billing-profiles-invoices-and-payments#h_01HWXFX3M45E8M87CD8ARRRKHZ"
            textStyle="global/body-secondary"
            linkStyle="monochrome"
            testID="billing-profile-learn-more-link"
          >
            <Trans>Learn more</Trans>
          </Link>
        </Stack>
      </MediaQuery>
      <div className={styles.filters}>
        <ProfileNameFilter
          aria-label={t("Profile name")}
          profileNameOptions={profileNameOptions}
        />
        <DateRangeFilter
          label={t("Issued at")}
          testID="issued-at-filter"
          aria-label={t("Issued at")}
        />
        <InvoiceStatusFilter />
        <ClearFilters />
      </div>
      <MediaQuery sm type="hide">
        <DataTable
          aria-label="invoice-table"
          columns={invoiceColumns}
          rows={data?.results || []}
          onSortChange={(sortDescriptor) =>
            setParam("ordering", sortDescriptorToOrderingParam(sortDescriptor))
          }
          sortDescriptor={orderingParamToSortDescriptor(flatParams.ordering)}
        />
      </MediaQuery>
      <MediaQuery sm type="show">
        <Container layout="full-width">
          {!isLoading ? (
            data?.results &&
            data.results.map((invoice, i) => {
              return (
                <HistoricalTableDataCard
                  number={invoice.number}
                  profileName={invoice.billing_profile?.profile_name || "-"}
                  total={
                    canViewClientPortalPricing
                      ? formatInvoiceTotal(invoice.total_amount)
                      : ""
                  }
                  issuedAt={invoice.issued_at || "-"}
                  status={invoice.status || "-"}
                  id={invoice.id}
                  key={i}
                />
              );
            })
          ) : (
            <Loader />
          )}
        </Container>
      </MediaQuery>
      <Pagination
        total={data?.count ? data.count : 0}
        page={flatParams["page"] ? Number(flatParams["page"]) : 1}
        pageCount={
          flatParams["page_size"] ? Number(flatParams["page_size"]) : 10
        }
      />
    </>
  );
}

const styles = {
  filters: css({
    display: "flex",
    rowGap: "var(--space-3)",
    columnGap: "var(--space-2)",
    flexWrap: "wrap",
    flexShrink: 1,
    flexGrow: 1,
    alignItems: "flex-start",
  }),
};
